import { type Scope, createContextScope } from '@radix-ui/react-context'
import { useCallback, useState } from 'react'
import type { PressableProps } from 'react-native'
import { ContentActions } from './components/content-actions'
import { ContentActionsText } from './components/content-actions-text'
import { ContentIcon } from './components/content-icon'
import { ContentLink } from './components/content-link'
import { ContentSubtitle } from './components/content-subtitle'
import { ContentTitle } from './components/content-title'
import * as Comp from './content.styles'
import type { ContentProps } from './content.types'

export const DISPLAY_NAME = 'Content'

type ContentRootProps = ContentProps & PressableProps

function ContentRoot(props: ContentRootProps) {
  const { children, type, size, testID, __scopeContent } =
    props as ScopedProps<ContentRootProps>

  const ContentProviderProps = {
    size: size,
    type: type,
    scope: __scopeContent,
  } as const

  const [hasPressed, setHasPressed] = useState(false)
  const handlePress = useCallback((press: 'in' | 'out') => {
    setHasPressed(press === 'in')
  }, [])

  return (
    <ContentProvider {...ContentProviderProps}>
      <Comp.Root
        onPressIn={() => handlePress('in')}
        onPressOut={() => handlePress('out')}
        hasPressed={hasPressed}
        testID={testID}
        disabled={type === 'title'}
      >
        {children}
      </Comp.Root>
    </ContentProvider>
  )
}

/*
----------------------------------------------------------------
Scope Definition
----------------------------------------------------------------
*/

export type ScopedProps<P> = P & {
  __scopeContent?: Scope
}

type ContentContext = ContentProps

const [createContentContext] = createContextScope(DISPLAY_NAME)

export const [ContentProvider, useContentContext] =
  createContentContext<ContentContext>(DISPLAY_NAME)

/*
----------------------------------------------------------------
Composition Export
----------------------------------------------------------------
*/

export const Content = {
  Root: ContentRoot,
  Icon: ContentIcon,
  ActionsText: ContentActionsText,
  Title: ContentTitle,
  Subtitle: ContentSubtitle,
  Actions: ContentActions,
  Link: ContentLink,
}
